import {
  Audience,
  AudienceWithCompletionCriteria,
  ExperienceAnchor,
  ExperienceTemplateStatus,
  IExperienceContact,
  IExperienceTemplate,
  IExperienceTemplateResource,
  ISupportingActor,
  ITaskTemplate
} from '@dadohr/shared/domainTypes/experience';
import { OrganizationOverrides } from '@dadohr/shared/domainTypes/org';
import { LucideIcon as Icon } from 'lucide-react';
import { useMemo } from 'react';

export type TaskBuilderContext = {
  templateId: string;
  orgId: string;
  title: string;
  adminTitle: string;
  contacts: IExperienceContact[];
  notificationSender: {
    name: string;
    replyTo: string;
  };
  status: ExperienceTemplateStatus;
  supportingActors: { [key: string]: ISupportingActor };
  anchor: ExperienceAnchor;
  tz: string;

  orgOverrides: OrganizationOverrides | null;
  tasks: ITaskTemplate[];
};

export type MinimalTaskBuilderContext = Omit<
  TaskBuilderContext,
  | 'templateId'
  | 'orgId'
  | 'adminTitle'
  | 'notificationSender'
  | 'status'
  | 'tasks' // TODO TRIGGERED ITEMS not sure if this is required here, we'll see
>;

export function getTimelineStartTimeFromTaskBuilderContext(
  context: TaskBuilderContext
): { hour: number; minute: number } | null {
  if (context.anchor.offset.type !== 'ABSOLUTE') {
    return null;
  }

  return {
    hour: context.anchor.offset.hour,
    minute: context.anchor.offset.minute ?? 0
  };
}

export const useTaskBuilderContext = (
  d: IExperienceTemplate
): TaskBuilderContext => {
  const {
    id: templateId,
    orgId,
    title,
    adminTitle,
    contacts,
    notificationSender,
    status,
    supportingActors,
    anchor,
    tz,
    overrides,
    tasks
  } = d;
  const orgOverrides = overrides?.org || null;
  return useMemo(
    () => ({
      templateId,
      orgId,
      title,
      adminTitle,
      contacts,
      notificationSender,
      supportingActors,
      status,
      anchor,
      tz,
      orgOverrides,
      tasks
    }),
    [
      templateId,
      orgId,
      title,
      adminTitle,
      contacts,
      notificationSender,
      status,
      supportingActors,
      anchor,
      tz,
      orgOverrides,
      tasks
    ]
  );
};

export type ViewModeSettingsOverview = {
  mode: 'SETTINGS_OVERVIEW';
};
export type ViewModeAudiencesOverview = {
  mode: 'AUDIENCES_OVERVIEW';
  previewAudienceId: string | null;
};
export type ViewModeTaskAudiencesDetail = {
  mode: 'AUDIENCES_DETAIL';
  editModel: AudienceWithCompletionCriteria;
  showHiddenBlocks: boolean;
};

export type ViewModeAudiencesDetail = {
  mode: 'AUDIENCES_DETAIL';
  editModel: Audience;
  showHiddenBlocks: boolean;
};
export type ViewModeAssignment = {
  mode: 'ASSIGNMENT';
};
export type ViewModeTriggeredItems = {
  mode: 'TRIGGERED_ITEMS';
};
export type ViewModeStartAndDuration = {
  mode: 'START_AND_DURATION';
};
export type ViewModeReminders = {
  mode: 'REMINDERS';
};

export type ViewModeSendDate = {
  mode: 'SEND_DATE';
};

export type ViewModeRecipient = {
  mode: 'RECIPIENT';
};

export type ViewModeTrigger = {
  mode: 'TRIGGER';
};

export type ViewModeTaskList = {
  mode: 'TASK_LIST';
};

export type TaskBuilderViewMode =
  | ViewModeSettingsOverview
  | ViewModeAudiencesOverview
  | ViewModeTaskAudiencesDetail
  | ViewModeAssignment
  | ViewModeTriggeredItems
  | ViewModeStartAndDuration
  | ViewModeReminders;

export const TASK_BUILDER_VIEW_MODES: {
  settingsOverview: ViewModeSettingsOverview;
  audiencesOverview: (
    previewAudienceId: string | null
  ) => ViewModeAudiencesOverview;
  audiencesDetail: (
    editModel: AudienceWithCompletionCriteria,
    showHiddenBlocks: boolean
  ) => ViewModeTaskAudiencesDetail;
  assignment: ViewModeAssignment;
  startandDuration: ViewModeStartAndDuration;
  triggeredItems: ViewModeTriggeredItems;
  reminders: ViewModeReminders;
} = {
  settingsOverview: {
    mode: 'SETTINGS_OVERVIEW'
  },
  audiencesOverview: (previewAudienceId) => ({
    mode: 'AUDIENCES_OVERVIEW',
    previewAudienceId
  }),
  audiencesDetail: (editModel, showHiddenBlocks) => ({
    mode: 'AUDIENCES_DETAIL',
    editModel,
    showHiddenBlocks
  }),
  assignment: { mode: 'ASSIGNMENT' },
  startandDuration: { mode: 'START_AND_DURATION' },
  triggeredItems: { mode: 'TRIGGERED_ITEMS' },
  reminders: { mode: 'REMINDERS' }
};

export type MessageBuilderViewMode =
  | ViewModeSettingsOverview
  | ViewModeRecipient
  | ViewModeSendDate
  | ViewModeAudiencesOverview
  | ViewModeAudiencesDetail
  | ViewModeTaskList;

export const MESSAGE_BUILDER_VIEW_MODES: {
  settingsOverview: ViewModeSettingsOverview;
  audiencesOverview: (
    previewAudienceId: string | null
  ) => ViewModeAudiencesOverview;
  audiencesDetail: (
    editModel: Audience,
    showHiddenBlocks: boolean
  ) => ViewModeAudiencesDetail;
  recipient: ViewModeRecipient;
  sendDate: ViewModeSendDate;
  taskList: ViewModeTaskList;
} = {
  settingsOverview: {
    mode: 'SETTINGS_OVERVIEW'
  },
  audiencesOverview: (previewAudienceId) => ({
    mode: 'AUDIENCES_OVERVIEW',
    previewAudienceId
  }),
  audiencesDetail: (editModel, showHiddenBlocks) => ({
    mode: 'AUDIENCES_DETAIL',
    editModel,
    showHiddenBlocks
  }),
  recipient: { mode: 'RECIPIENT' },
  sendDate: { mode: 'SEND_DATE' },
  taskList: { mode: 'TASK_LIST' }
};

export type AutomationBuilderViewMode =
  | ViewModeSettingsOverview
  | ViewModeTrigger
  | ViewModeAudiencesOverview
  | ViewModeAudiencesDetail;

export const AUTOMATION_BUILDER_VIEW_MODES: {
  settingsOverview: ViewModeSettingsOverview;
  trigger: ViewModeTrigger;
  audiencesOverview: (
    previewAudienceId: string | null
  ) => ViewModeAudiencesOverview;
  audiencesDetail: (
    editModel: Audience,
    showHiddenBlocks: boolean
  ) => ViewModeAudiencesDetail;
} = {
  settingsOverview: {
    mode: 'SETTINGS_OVERVIEW'
  },
  trigger: { mode: 'TRIGGER' },
  audiencesOverview: (previewAudienceId) => ({
    mode: 'AUDIENCES_OVERVIEW',
    previewAudienceId
  }),
  audiencesDetail: (editModel, showHiddenBlocks) => ({
    mode: 'AUDIENCES_DETAIL',
    editModel,
    showHiddenBlocks
  })
};

export type BlockSettingsSubPage = {
  header: {
    def: {
      label: React.ReactNode;
      description?: React.ReactNode;
      icon?: Icon;
      iconSrc?: string;
    };
  };
  body: React.ComponentType<{
    onBack: () => void;
    resources: IExperienceTemplateResource[];
    onChangeResources: (nextResources: IExperienceTemplateResource[]) => void;
  }>;
};
